import { productPartNumbersStyles, ProductPartNumbersStyles } from "@bToC/Overrides/Components/ProductPartNumbers";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    separator = "separator",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.separator]: string;
    };
    productNumber: string;
    customerProductNumber?: string;
    manufacturerItem?: string;
    showCustomerName?: boolean;
    showLabel?: boolean;
    showManufacturerItem?: boolean;
    extendedStyles?: ProductPartNumbersStyles;
}

type Props = OwnProps & WidgetProps & HasProduct;

export interface ProductDetailsPartNumbersBarStyles {
    wrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    condensed?: boolean;
    container?: GridContainerProps;
    erpNumberGridItem?: GridItemProps;
    erpNumberLabelText?: TypographyPresentationProps;
    erpNumberValueText?: TypographyPresentationProps;
    customerNameGridItem?: GridItemProps;
    customerNameLabelText?: TypographyPresentationProps;
    customerNameValueText?: TypographyPresentationProps;
    manufacturerItemGridItem?: GridItemProps;
    manufacturerItemLabelText?: TypographyPresentationProps;
    manufacturerItemValueText?: TypographyPresentationProps;
    link?: LinkPresentationProps;
}

export const productDetailsPartNumbersBarStyles: ProductDetailsPartNumbersBarStyles = {
    wrapper: {
        css: css`
            padding-bottom: 15px;
        `,
    },
    attributeTypeLabelText: {
        weight: "bold",
    },
    attributeTypeValuesText: {
        css: css`
            margin-bottom: 5px;
        `,
    },
    container: { gap: 5 },
    erpNumberLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
        `,
    },
    erpNumberValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
        `,
    },
    customerNameLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
        `,
    },
    customerNameValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
        `,
    },
    manufacturerItemLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
        `,
    },
    manufacturerItemValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
        `,
    },
    link: {
        css: css`
            text-decoration:underline;
            font-weight: bold;
        `
    }
};

const styles = productDetailsPartNumbersBarStyles;

const ProductDetailsPartNumbersBar: React.FC<Props> = ({ fields, product: { brand, attributeTypes, productNumber, manufacturerItem, customerProductNumber },
    showCustomerName = true,
    showManufacturerItem = true,
    showLabel = true,
    extendedStyles, }) => {
    if (!brand && (!attributeTypes || attributeTypes.length === 0)) {
        return null;
    }

    const [styles] = React.useState(() => mergeToNew(productDetailsPartNumbersBarStyles, extendedStyles));
    const displayManufacturerNumber = showManufacturerItem && manufacturerItem;
    const displayCustomerNumber = showCustomerName && customerProductNumber;
    const numberOfItems = 1 + (displayManufacturerNumber ? 1 : 0) + (displayCustomerNumber ? 1 : 0);
    const mobileGridItemWidth = numberOfItems > 1 ? 6 : 12;
    const desktopGridItemWidth = 12 / numberOfItems;
    const gridItemWidth = styles.condensed
        ? [mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, desktopGridItemWidth]
        : 12;
    const separators = [
        productNumber && customerProductNumber, customerProductNumber && manufacturerItem, brand && manufacturerItem
    ]
    const list = [productNumber && (
        <div>
            {showLabel && <Typography {...styles.erpNumberLabelText}>{translate("Part #")}&nbsp;</Typography>}
            <Typography {...styles.erpNumberValueText} data-test-selector="productNumber">
                {productNumber}
            </Typography>
        </div>
    ),
    customerProductNumber && (
        <div>
            {showLabel && <Typography {...styles.customerNameLabelText}>{translate("My Part #")}&nbsp;</Typography>}
            <Typography {...styles.customerNameValueText}>{customerProductNumber}</Typography>
        </div>
    ),
    manufacturerItem && (
        <div>
            {showLabel && <Typography {...styles.manufacturerItemLabelText}>{translate("MFG #")}&nbsp;</Typography>}
            <Typography {...styles.manufacturerItemValueText}>{manufacturerItem}</Typography>
        </div>
    ),
    brand && (
        <div> <span>
            {showLabel && <Typography {...styles.manufacturerItemLabelText}>{translate("Mfr:")}&nbsp;</Typography>}
            <Typography as="a">
                <Link {...styles.link} href={`${brand.detailPagePath}/Catalog/All`} rel="noopener noreferrer" target="_blank">{brand.name}</Link>
            </Typography>
        </span>

        </div>
    )].filter(p=>p?.toString().trim());

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="productDetails_infobar">
            <GridContainer {...styles.container}>
                {list.map((item,index) => (
                    <>
                    {(index>0 && index<list.length) && (<span>&nbsp;{fields.separator}&nbsp;</span>)}
                    {item}
                    </>
                ))}
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsPartNumbersBar),
    definition: {
        displayName: "Product Info Bar",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.separator,
                displayName: "Separator to divide the info",
                editorTemplate: "TextField",
                defaultValue: "|",
                fieldType: "General",
            },
        ],
    }
};

export default widgetModule;
